import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from './components/Header';
import FileUpload from './components/FileUpload';
import FormatSelector from './components/FormatSelector';
import ImagePreview from './components/ImagePreview';
import ConversionHistory from './components/ConversionHistory';
import AuthModal from './components/AuthModal';
import ErrorAlert from './components/ErrorAlert';
import { convertImage, downloadImage, detectImageFormat, SUPPORTED_FORMATS } from './utils/imageConverter';
import { logConversion, signOut, getCurrentUser } from './utils/supabaseHelpers';
import { supabase, isSupabaseEnabled } from './config/supabase';

function App() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('');
  const [convertedBlob, setConvertedBlob] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(0);

  useEffect(() => {
    // Check for existing user session
    const checkUser = async () => {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    };

    checkUser();

    // Listen for auth changes
    if (isSupabaseEnabled) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        setUser(session?.user || null);
      });

      return () => subscription.unsubscribe();
    }
  }, []);

  const handleFileSelect = (file, errorMessage) => {
    if (errorMessage) {
      setError(errorMessage);
      return;
    }
    setSelectedFile(file);
    setSelectedFormat('');
    setConvertedBlob(null);
    setError('');
  };

  const handleFormatChange = (format) => {
    setSelectedFormat(format);
    setConvertedBlob(null);
  };

  const handleConvert = async () => {
    if (!selectedFile || !selectedFormat) return;

    setIsConverting(true);
    setConvertedBlob(null);
    setError('');
    setConversionProgress(0);

    try {
      const sourceFormat = detectImageFormat(selectedFile);
      const blob = await convertImage(
        selectedFile,
        selectedFormat,
        (progress) => setConversionProgress(Math.round(progress * 100))
      );
      setConvertedBlob(blob);

      // Log conversion if user is signed in
      if (user) {
        await logConversion(sourceFormat, selectedFormat, selectedFile.size, user.id);
      }
    } catch (err) {
      setError(err.message || 'Fehler beim Konvertieren des Bildes');
    } finally {
      setIsConverting(false);
      setConversionProgress(0);
    }
  };

  const handleDownload = () => {
    if (!convertedBlob || !selectedFile || !selectedFormat) return;

    const filename = selectedFile.name.split('.')[0] || 'konvertiert';
    downloadImage(convertedBlob, filename, selectedFormat);
  };

  const handleSignOut = async () => {
    await signOut();
    setUser(null);
  };

  const handleAuthSuccess = (newUser) => {
    setUser(newUser);
    setShowAuthModal(false);
  };

  const canConvert = selectedFile && selectedFormat && !isConverting;

  return (
    <div className="min-h-screen bg-white">
      <Header 
        user={user} 
        onSignOut={handleSignOut}
      />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Error Alert */}
          <ErrorAlert error={error} onDismiss={() => setError('')} />

          {/* Main Conversion Interface */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
            {/* Description Text */}
            <div className="text-center mb-6">
              <p className="text-primary-600 text-lg">
                Lade dein Bild hoch und konvertiere es zu JPEG, PNG, WebP, GIF, BMP, TIFF, AVIF oder SVG.
                Alle Verarbeitungen finden in deinem Browser statt - keine Dateien werden auf unseren Servern gespeichert.
              </p>
            </div>

            {/* File Upload */}
            <FileUpload onFileSelect={handleFileSelect} disabled={isConverting} />

            {/* Format Selection */}
            {selectedFile && (
              <FormatSelector
                sourceFormat={detectImageFormat(selectedFile)}
                selectedFormat={selectedFormat}
                onFormatChange={handleFormatChange}
                disabled={isConverting}
              />
            )}

            {/* Convert Button */}
            {selectedFile && selectedFormat && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-center"
              >
                <button
                  onClick={handleConvert}
                  disabled={!canConvert}
                  className="px-8 py-3 bg-button text-white font-medium rounded-lg hover:bg-button-hover focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isConverting ? `Konvertiere... ${conversionProgress}%` : 'Bild konvertieren'}
                </button>
              </motion.div>
            )}

            {/* Image Preview */}
            {selectedFile && (
              <ImagePreview
                originalFile={selectedFile}
                convertedBlob={convertedBlob}
                targetFormat={selectedFormat}
                onDownload={handleDownload}
                isConverting={isConverting}
              />
            )}
          </div>

          {/* Conversion History */}
          {isSupabaseEnabled && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <ConversionHistory user={user} />
            </div>
          )}

          {/* Features Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-semibold text-primary-600 mb-4">Funktionen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <h4 className="font-medium text-primary-600">Client-seitige Verarbeitung</h4>
                <p className="text-sm text-primary-500">
                  Alle Konvertierungen finden in deinem Browser statt. Keine Dateien werden auf unsere Server hochgeladen.
                </p>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium text-primary-600">Mehrere Formate</h4>
                <p className="text-sm text-primary-500">
                  Konvertiere zwischen JPEG, PNG, WebP, GIF, BMP, TIFF, AVIF und SVG Formaten.
                </p>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium text-primary-600">Schnell & Sicher</h4>
                <p className="text-sm text-primary-500">
                  Schnelle Konvertierungen ohne dass Daten dein Gerät verlassen, außer du meldest dich an.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </main>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={handleAuthSuccess}
      />
    </div>
  );
}

export default App;